// Hook fuer /jobs/stream mit automatischem Reconnect + Polling-Fallback.

import { useContext, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { TranscriptionContext, normalizeServerJob } from '../../context/TranscriptionContext';
import { fetchJobs, openJobsStream } from '../../helper/jobsApi';

const FEATURE_FLAG_DEFAULT = 'true';
const FALLBACK_POLL_MS = 10000;
const SNAPSHOT_SYNC_MS = 15000;
const RECONNECT_MIN_MS = 2000;
const RECONNECT_MAX_MS = 30000;

function flagEnabled() {
  const raw = (
    process.env.REACT_APP_JOBS_STREAM_ENABLED ?? FEATURE_FLAG_DEFAULT
  )
    .toString()
    .toLowerCase();
  return ['1', 'true', 'yes', 'on'].includes(raw);
}

export default function useJobsStream({ enabled }) {
  const { instance, accounts } = useMsal();
  const isSignedIn = accounts && accounts.length > 0;
  const { replaceJobsSnapshot, upsertJob } = useContext(TranscriptionContext);
  const abortRef = useRef(null);
  const pollTimerRef = useRef(null);
  const syncTimerRef = useRef(null);
  const reconnectRef = useRef(RECONNECT_MIN_MS);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !isSignedIn) return undefined;

    stoppedRef.current = false;
    const useStream = flagEnabled();

    const handleEvent = (evt) => {
      if (!evt || !evt.type) return;
      if (evt.type === 'snapshot' && Array.isArray(evt.jobs)) {
        replaceJobsSnapshot(evt.jobs);
        return;
      }
      if (evt.type === 'job_update' && evt.job) {
        const normalized = normalizeServerJob(evt.job);
        upsertJob(normalized.jobId, normalized);
      }
    };

    const pollOnce = async () => {
      try {
        const list = await fetchJobs(instance);
        replaceJobsSnapshot(list);
      } catch (err) {
        if (err?.name === 'AuthSessionExpiredError') {
          stoppedRef.current = true;
          stopPolling();
          stopSnapshotSync();
          return;
        }
        console.warn('[jobs] polling failed', err);
      }
    };

    const startPolling = () => {
      if (pollTimerRef.current) return;
      pollOnce();
      pollTimerRef.current = setInterval(pollOnce, FALLBACK_POLL_MS);
    };

    const stopPolling = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const startSnapshotSync = () => {
      if (syncTimerRef.current) return;
      syncTimerRef.current = setInterval(pollOnce, SNAPSHOT_SYNC_MS);
    };

    const stopSnapshotSync = () => {
      if (syncTimerRef.current) {
        clearInterval(syncTimerRef.current);
        syncTimerRef.current = null;
      }
    };

    const connectStream = async () => {
      if (stoppedRef.current) return;
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        await openJobsStream({
          instance,
          signal: controller.signal,
          onEvent: handleEvent,
          onError: (err) => console.warn('[jobs] stream error', err),
        });
      } catch (err) {
        if (stoppedRef.current || err?.name === 'AbortError') return;
        if (err?.name === 'AuthSessionExpiredError') {
          stoppedRef.current = true;
          stopPolling();
          stopSnapshotSync();
          return;
        }
        console.warn('[jobs] stream ended, reconnect in', reconnectRef.current, 'ms');
        // Waehrend Disconnect kurzzeitig auf Polling zurueckfallen
        startPolling();
        await new Promise((r) => setTimeout(r, reconnectRef.current));
        reconnectRef.current = Math.min(
          reconnectRef.current * 2,
          RECONNECT_MAX_MS,
        );
        if (!stoppedRef.current) {
          stopPolling();
          connectStream();
        }
        return;
      }
      // Erfolgreich beendet (Server close) -> reconnect mit Reset
      reconnectRef.current = RECONNECT_MIN_MS;
      if (!stoppedRef.current) connectStream();
    };

    // Vor dem Stream immer einmal den Server-Snapshot ziehen.
    // Das bereinigt stale sessionStorage-Zustaende nach Reload/Login sofort,
    // selbst wenn der SSE-Stream erst spaeter ankommt oder kurz scheitert.
    pollOnce();
    // Zusaetzliche Re-Synchronisierung: falls ein SSE-Event verloren geht,
    // korrigiert der periodische Snapshot den Status trotzdem zeitnah.
    startSnapshotSync();

    if (useStream) {
      connectStream();
    } else {
      startPolling();
    }

    return () => {
      stoppedRef.current = true;
      stopPolling();
      stopSnapshotSync();
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch (e) {
          /* ignore */
        }
        abortRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isSignedIn, instance]);
}
